
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import PreviewDialog from '@/ui/PreviewDialog.vue';
import { ArialFont } from '@/assets/fonts/Arial';
import { VerdanaFont } from '@/assets/fonts/Verdana';
import { ComicSansFont } from '@/assets/fonts/ComicSans';
import { OpenSansFont } from '@/assets/fonts/OpenSans-Regular';

@Component({
    components: { PreviewDialog },
})
export default class PageRenderPortal extends Vue {
    @Prop({ type: String, required: true })
    public readonly paperType!: string;

    @Prop({ type: String, required: true })
    public readonly orientation!: string;

    @Prop({ type: Number, required: true })
    public readonly selectedPage!: number;

    @Prop({ type: Number, required: true })
    public readonly generatePdfCounter!: number;

    @Prop({ type: Number, required: true })
    public readonly showPreviewCounter!: number;

    public showPreviewDialog = false;
    public previews: string[] = [];

    public get pageClasses(): any {
        const frameClass = 'mjegb-page-frame-' + this.orientation + '-' + this.paperType;
        return {
            [frameClass]: true,
        };
    }

    private scrollFixQueued = false;

    @Watch('paperType', { immediate: true })
    public onPaperTypeChanged(): void {
        this.fixScrollPosition();
    }

    @Watch('orientation', { immediate: true })
    public onOrientationChanged(): void {
        this.fixScrollPosition();
    }

    @Watch('selectedPage', { immediate: true })
    public onSelectedPageChanged(): void {
        this.fixScrollPosition();
    }

    @Watch('generatePdfCounter')
    public onGeneratePdf(): void {
        const portalRef: any = this.$refs.portal;
        if (portalRef) {
            (window as any).html2canvas = html2canvas;
            const pdf = new jsPDF(this.orientation, 'pt', this.paperType);
            pdf.addFileToVFS('Arial.ttf', ArialFont);
            pdf.addFont('Arial.ttf', 'arial', 'normal');
            pdf.addFileToVFS('Verdana.ttf', VerdanaFont);
            pdf.addFont('Verdana.ttf', 'verdana', 'normal');
            pdf.addFileToVFS('ComicSans.ttf', ComicSansFont);
            pdf.addFont('ComicSans.ttf', 'comic sans ms', 'normal');
            pdf.addFileToVFS('OpenSans-Regular.ttf', OpenSansFont);
            pdf.addFont('OpenSans-Regular.ttf', 'opensans', 'normal');
            const sH = portalRef.offsetHeight;
            const dH = pdf.internal.pageSize.getHeight();
            const scale = dH / sH;
            // @ts-ignore
            pdf.html(portalRef, {
                html2canvas: {
                    scale: scale,
                    scrollX: 0,
                    scrollY: 0,
                    imageTimeout: 0,
                    useCORS: true,
                    ignoreElements: (element: Element) => element.classList.contains('skip-pdf'),
                },
                callback: (pdf: jsPDF) => {
                    pdf.save('boekje.pdf', { returnPromise: true });
                },
            });
        }
    }

    @Watch('showPreviewCounter')
    public onShowPreview(): void {
        const portalRef: any = this.$refs.portal;
        this.showPreviewDialog = false;
        this.previews = [];
        if (portalRef && portalRef.children.length > 0) {
            const count = portalRef.children.length;
            const generate = (index: number) => {
                html2canvas(portalRef.children[index], {
                    scale: 1,
                    scrollX: 0,
                    scrollY: 0,
                    imageTimeout: 0,
                    useCORS: true,
                    ignoreElements: (element: Element) => element.classList.contains('skip-pdf'),
                }).then((canvas) => {
                    this.previews.push(canvas.toDataURL('image/png', 1));
                    if (index + 1 < count) {
                        generate(index + 1);
                    } else {
                        this.showPreviewDialog = true;
                    }
                });
            };
            generate(0);
        }
    }

    private fixScrollPosition(): void {
        if (this.scrollFixQueued) {
            return;
        }
        this.scrollFixQueued = true;
        this.$nextTick(() => {
            const portalRef: any = this.$refs.portal;
            if (portalRef) {
                portalRef.scrollTop = this.selectedPage * portalRef.offsetHeight;
            }
            this.scrollFixQueued = false;
        });
    }
}
